import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FileService, IStorage, LocalStorage } from '@isophi/core-legacy';
import { DocumentPreviewService, IconService, ToastService } from '@isophi/core-ui';
import { PortfolioManagerReportsService, TargetListReport } from '@isophi/isophi-pdfs-api';
import { ManagerTarget, PaginatedManagerTargetList, PortfolioService } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable, switchMap, takeUntil, tap } from 'rxjs';

import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { HelperService } from '../../../../core/services/helper.service';
import { BaseComponent } from '../../../../shared/components/base/base.component';
import { LocalStorageConstService } from '../../../../shared/services/local-storage-const.service';
import { PlanFilterCriteria } from '../../constants/models';

@Component({
  selector: 'app-pap-plan',
  templateUrl: './pap-plan.component.html',
  styleUrls: ['./pap-plan.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PapPlanComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly helperService = inject(HelperService);
  private readonly translocoService = inject(TranslocoService);
  protected readonly iconService = inject(IconService);
  private readonly router = inject(Router);
  private readonly routerLinkFactory = inject(RouterLinkFactory);
  private readonly planService = inject(PortfolioService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly toastService = inject(ToastService);
  private readonly fileService = inject(FileService);
  private readonly documentPreviewService = inject(DocumentPreviewService);
  private readonly portfolioManagerReportsService = inject(PortfolioManagerReportsService);
  private readonly lSConstService = inject(LocalStorageConstService);
  private readonly localStorage: IStorage = new LocalStorage();

  portfolioTargetList$: Observable<PaginatedManagerTargetList>;

  private portfolioTargetReload$ = new BehaviorSubject<void>(null);

  private filterCriteria: PlanFilterCriteria = {
    search: null,
    teacherId: null,
    state: '',
    dateQuickRangeKey: null,
    dateStart: '',
    dateEnd: '',
  };

  protected page = 1;
  protected limit = 10;
  protected offset = 0;

  ngOnInit(): void {
    this.helperService.setTitle(undefined, this.translocoService.translate('pap/plan/title'));
    const saved = this.localStorage.getItem(this.lSConstService.papPlanPageLimit);
    if (saved) {
      this.limit = +saved;
    }
    this.loadData();
  }

  ngOnDestroy(): void {
    this.helperService.clearSmallTextTitle();
    super.ngOnDestroy();
  }

  loadData(): void {
    this.portfolioTargetList$ = this.portfolioTargetReload$.pipe(
      switchMap(() =>
        this.planService.portfolioTargetsList(
          undefined,
          this.filterCriteria.dateStart,
          this.filterCriteria.dateEnd,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          this.limit,
          this.offset,
          this.filterCriteria.search,
          this.filterCriteria.state,
          undefined,
          this.filterCriteria.teacherId
        )
      ),
      tap((portfolioTargetList) => {
        const portfolioTargetText =
          portfolioTargetList.count > 1
            ? this.translocoService.translate('pap/plan/portfolioTargets')
            : this.translocoService.translate('pap/plan/portfolioTarget');
        if (portfolioTargetList.count > 0) {
          this.helperService.setSmallTextTitle(`${portfolioTargetList.count} ${portfolioTargetText}`);
        }
      })
    );
  }

  openPortfolioTarget(portfolioTarget: ManagerTarget): void {
    this.router.navigate(this.routerLinkFactory.papPlanDetail(portfolioTarget.id));
  }

  onPageChange(page: number): void {
    this.offset = (page - 1) * this.limit;
    this.reloadPortfolioActivities();
  }

  onLimitChange(value: number): void {
    this.limit = value;
    this.page = 1;
    this.offset = 0;
    this.localStorage.setItem(this.lSConstService.papPlanPageLimit, String(value));
    this.reloadPortfolioActivities();
  }

  reloadPortfolioActivities(): void {
    this.portfolioTargetReload$.next();
  }

  printPage(): void {
    this.spinner.show();
    const body: TargetListReport = {
      query: {
        created__date__gte: this.filterCriteria.dateStart,
        created__date__lte: this.filterCriteria.dateEnd,
        search: this.filterCriteria.search,
        state: this.filterCriteria.state,
        teacher_id: this.filterCriteria.teacherId,
      },
    };
    this.portfolioManagerReportsService
      .getManagerTargetListReport(body)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (data: Blob) => {
          this.documentPreviewService.openBlob(data, `${this.translocoService.translate('file/targets')}.pdf`, 'application/pdf');
        },
        error: () => {
          this.toastService.error(this.translocoService.translate('pap/plan/exportErrorToast'));
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }

  exportPage(type: 'xlsx' | 'csv'): void {
    this.spinner.show();
    this.planService
      .portfolioTargetsPrintRetrieve(
        undefined,
        this.filterCriteria.dateStart,
        this.filterCriteria.dateEnd,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        this.filterCriteria.search,
        this.filterCriteria.state,
        undefined,
        this.filterCriteria.teacherId,
        type
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (data: Blob) => {
          this.fileService.downloadFile(
            data,
            `${this.translocoService.translate('file/targets')}.xlsx`,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          );
        },
        error: () => {
          this.toastService.error(this.translocoService.translate('pap/plan/exportErrorToast'));
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }

  setFilter(filterCriteria: PlanFilterCriteria): void {
    this.filterCriteria = filterCriteria;
    this.page = 1;
    this.offset = 0;
    this.reloadPortfolioActivities();
  }
}
